import React, { useEffect, useState } from "react";
import { Gauge, Plus } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { Spinner } from "components/ui/spinner";
import { Separator } from "components/ui/separator";
import { MeterRoleRow } from "./MeterRoleRow";
import { MeterFieldMappingRow } from "./MeterFieldMappingRow";
import {
  AssetTypeOption,
  DocumentMeterSettings,
  FieldDefinitionOption,
  MeterFieldMapping,
  MeterRole,
} from "./types";

const METERS_CATEGORY_NAME = "Meters";

export const MetersDocumentenTab: React.FC = () => {
  const { session } = useSession();
  const [settings, setSettings] = useState<DocumentMeterSettings | null>(null);
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitionOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (session) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadAll = async () => {
    try {
      setIsLoading(true);

      const [settingsRes, categoriesRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/document-meter-settings`, { method: "GET" }, session),
        fetchWithAuth(`${API_URL}/asset-categories`, { method: "GET" }, session),
      ]);

      if (!settingsRes.ok) throw new Error(`Settings ophalen mislukt (${settingsRes.status})`);
      if (!categoriesRes.ok) throw new Error(`Categorieën ophalen mislukt (${categoriesRes.status})`);

      const settingsData: DocumentMeterSettings = await settingsRes.json();
      const categories: { id: number; name: string }[] = await categoriesRes.json();
      const metersCategory = categories.find((c) => c.name === METERS_CATEGORY_NAME);

      if (metersCategory) {
        const [typesRes, detailRes] = await Promise.all([
          fetchWithAuth(`${API_URL}/asset-categories/${metersCategory.id}/types`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/asset-categories/${metersCategory.id}`, { method: "GET" }, session),
        ]);

        if (typesRes.ok) {
          const types: any[] = await typesRes.json();
          setAssetTypes(types.filter((t) => t.active !== false).map((t) => ({ id: t.id, name: t.name })));
        }
        if (detailRes.ok) {
          const detail: any = await detailRes.json();
          const defs: any[] = detail?.fieldDefinitions || [];
          setFieldDefinitions(
            defs
              .filter((d) => d.active !== false)
              .map((d) => ({ fieldName: d.fieldName, displayName: d.displayName })),
          );
        }
      } else {
        toast.warn(`Categorie "${METERS_CATEGORY_NAME}" niet gevonden — meter-types kunnen niet getoond worden`);
      }

      setSettings(settingsData);
      setHasChanges(false);
    } catch (error) {
      console.error("Error loading document meter settings:", error);
      toast.error("Kon meterinstellingen voor documenten niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const mutate = (next: DocumentMeterSettings) => {
    setSettings(next);
    setHasChanges(true);
  };

  const updateRole = (index: number, role: MeterRole) => {
    if (!settings) return;
    const roles = [...settings.roles];
    roles[index] = role;
    mutate({ ...settings, roles });
  };

  const addRole = () => {
    if (!settings) return;
    const newRole: MeterRole = { key: `rol-${Date.now()}`, label: "Nieuwe rol", assetTypeIds: [] };
    mutate({ ...settings, roles: [...settings.roles, newRole] });
  };

  const updateMapping = (index: number, mapping: MeterFieldMapping) => {
    if (!settings) return;
    const fieldMappings = [...settings.fieldMappings];
    fieldMappings[index] = mapping;
    mutate({ ...settings, fieldMappings });
  };

  const addMapping = () => {
    if (!settings) return;
    const newMapping: MeterFieldMapping = { documentField: "", source: { kind: "role" } };
    mutate({ ...settings, fieldMappings: [...settings.fieldMappings, newMapping] });
  };

  const removeMapping = (index: number) => {
    if (!settings) return;
    mutate({ ...settings, fieldMappings: settings.fieldMappings.filter((_, i) => i !== index) });
  };

  const updateScope = (patch: Partial<DocumentMeterSettings["scope"]>) => {
    if (!settings) return;
    mutate({ ...settings, scope: { ...settings.scope, ...patch } });
  };

  const save = async () => {
    if (!settings) return;
    try {
      setIsSaving(true);
      const response = await fetchWithAuth(
        `${API_URL}/document-meter-settings`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            roles: settings.roles,
            fieldMappings: settings.fieldMappings,
            scope: settings.scope,
          }),
        },
        session,
      );
      if (!response.ok) throw new Error(`Opslaan mislukt (${response.status})`);
      setHasChanges(false);
      toast.success("Meterinstellingen voor documenten opgeslagen");
    } catch (error) {
      console.error("Error saving document meter settings:", error);
      toast.error("Kon meterinstellingen voor documenten niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-row items-center gap-1.5">
        <Gauge className="h-[18px] w-[18px]" />
        <p className="text-lg">Meters in documenten</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document-rollen</CardTitle>
          <CardDescription>
            Bepaal welke voorziening-types als meter verschijnen in het huurcontract en de ingaande
            plaatsbeschrijving.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {settings.roles.map((role, index) => (
            <MeterRoleRow
              key={role.key}
              role={role}
              assetTypes={assetTypes}
              onChange={(r) => updateRole(index, r)}
            />
          ))}
          <Button type="button" variant="outline" size="sm" className="self-start" onClick={addRole}>
            <Plus className="h-4 w-4 mr-1" /> Rol toevoegen
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Veldkoppeling</CardTitle>
          <CardDescription>
            Welke gegevens van de meter (voorziening) komen in welk documentveld terecht. Geldt voor alle
            meters.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {settings.fieldMappings.map((mapping, index) => (
            <MeterFieldMappingRow
              key={index}
              mapping={mapping}
              fieldDefinitions={fieldDefinitions}
              onChange={(m) => updateMapping(index, m)}
              onRemove={() => removeMapping(index)}
            />
          ))}
          <Button type="button" variant="outline" size="sm" className="self-start" onClick={addMapping}>
            <Plus className="h-4 w-4 mr-1" /> Veldkoppeling toevoegen
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bereik &amp; oorsprong</CardTitle>
          <CardDescription>
            Welke meters meegenomen worden en hoe hun oorsprong benoemd wordt in het document.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between max-w-md">
            <div className="flex flex-col">
              <Label className="text-sm">Unit-meters meenemen</Label>
              <span className="text-xs text-muted-foreground">Privatieve meters van de unit</span>
            </div>
            <Switch
              checked={settings.scope.includeUnit}
              onCheckedChange={(v) => updateScope({ includeUnit: v })}
            />
          </div>
          <div className="flex items-center justify-between max-w-md">
            <div className="flex flex-col">
              <Label className="text-sm">Gebouw-meters meenemen</Label>
              <span className="text-xs text-muted-foreground">Gemeenschappelijke meters van het gebouw</span>
            </div>
            <Switch
              checked={settings.scope.includeBuilding}
              onCheckedChange={(v) => updateScope({ includeBuilding: v })}
            />
          </div>

          <Separator />

          <div className="flex flex-col gap-2 max-w-md">
            <Label className="text-sm" htmlFor="unitOriginLabel">
              Label unit-meters (oorsprong)
            </Label>
            <Input
              id="unitOriginLabel"
              value={settings.scope.unitOriginLabel}
              onChange={(e) => updateScope({ unitOriginLabel: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-2 max-w-md">
            <Label className="text-sm" htmlFor="buildingOriginLabel">
              Label gebouw-meters (oorsprong)
            </Label>
            <Input
              id="buildingOriginLabel"
              value={settings.scope.buildingOriginLabel}
              onChange={(e) => updateScope({ buildingOriginLabel: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {hasChanges && (
        <div className="flex gap-2">
          <Button onClick={save} disabled={isSaving}>
            {isSaving ? "Bezig met opslaan…" : "Opslaan"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MetersDocumentenTab;
